import { z } from 'zod';

export const CreateFlashDealSchema = z.object({
  title: z.string().min(3),
  slug: z.string().min(3),
  backgroundColor: z.string().optional(),
  textColor: z.string().optional(),
  bannerImageUrl: z.string().url().optional().or(z.literal('')),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  active: z.boolean().default(true),
  isFeatured: z.boolean().default(false),
});

export type CreateFlashDealRequest = z.infer<typeof CreateFlashDealSchema>;

export const UpdateFlashDealSchema = CreateFlashDealSchema.partial();
export type UpdateFlashDealRequest = z.infer<typeof UpdateFlashDealSchema>;

export const AddFlashDealProductSchema = z.object({
  productId: z.string(),
  discountType: z.enum(['PERCENTAGE', 'FLAT']),
  discountAmount: z.number().int().positive(),
});

export type AddFlashDealProductRequest = z.infer<typeof AddFlashDealProductSchema>;

export const UpdateFlashDealProductSchema = z.object({
  discountType: z.enum(['PERCENTAGE', 'FLAT']).optional(),
  discountAmount: z.number().int().positive().optional(),
});
export type UpdateFlashDealProductRequest = z.infer<typeof UpdateFlashDealProductSchema>;

const OptionalDateTimeSchema = z.string().datetime().optional().nullable();

export const CreateCouponSchema = z.object({
  code: z.string().trim().min(2).max(50),
  title: z.string().trim().max(120).optional().nullable(),
  discountType: z.enum(['PERCENTAGE', 'FLAT']),
  discountAmount: z.number().int().positive(),
  active: z.boolean().default(true),
  startsAt: OptionalDateTimeSchema,
  endsAt: OptionalDateTimeSchema,
  minSubtotalPaise: z.number().int().min(0).default(0),
  maxDiscountPaise: z.number().int().positive().optional().nullable(),
  usageLimit: z.number().int().positive().optional().nullable(),
  perUserLimit: z.number().int().positive().optional().nullable(),
});
export type CreateCouponRequest = z.infer<typeof CreateCouponSchema>;

export const UpdateCouponSchema = CreateCouponSchema.partial();
export type UpdateCouponRequest = z.infer<typeof UpdateCouponSchema>;

export const CreateDynamicPopupSchema = z.object({
  title: z.string().min(3),
  imageUrl: z.string().url().optional().or(z.literal('')),
  linkUrl: z.string().url().optional().or(z.literal('')),
  active: z.boolean().default(false),
  location: z.string().default('center'),
  delaySeconds: z.number().int().min(0).default(0),
  startsAt: z.string().datetime().optional().nullable(),
  endsAt: z.string().datetime().optional().nullable(),
});

export type CreateDynamicPopupRequest = z.infer<typeof CreateDynamicPopupSchema>;

export const UpdateDynamicPopupSchema = CreateDynamicPopupSchema.partial();
export type UpdateDynamicPopupRequest = z.infer<typeof UpdateDynamicPopupSchema>;

export const CreateNewsletterSchema = z.object({
  subject: z.string().min(1),
  bodyHtml: z.string().min(1),
  status: z.enum(['DRAFT', 'SCHEDULED', 'SENT']).default('DRAFT'),
  scheduledAt: z.string().datetime().optional().nullable(),
});
export type CreateNewsletterRequest = z.infer<typeof CreateNewsletterSchema>;

export const UpdateNewsletterSchema = CreateNewsletterSchema.partial();
export type UpdateNewsletterRequest = z.infer<typeof UpdateNewsletterSchema>;

export const CreateSubscriberSchema = z.object({
  email: z.string().email(),
  name: z.string().optional().nullable(),
  source: z.string().optional().nullable(),
  active: z.boolean().default(true),
});
export type CreateSubscriberRequest = z.infer<typeof CreateSubscriberSchema>;

export const UpdateSubscriberSchema = CreateSubscriberSchema.partial();
export type UpdateSubscriberRequest = z.infer<typeof UpdateSubscriberSchema>;

// ── Offers: seller-proposed flash deals, reviewed by the platform ───────────

/** Who bears an offer's discount. */
export const OfferFundingSchema = z.enum(['SELLER', 'SPLIT', 'PLATFORM']);
export type OfferFunding = z.infer<typeof OfferFundingSchema>;

export const OfferStatusSchema = z.enum(['PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'CANCELLED']);
export type OfferStatus = z.infer<typeof OfferStatusSchema>;

/** Terra Shoppy's share of an offer's discount for each funding choice, basis points. */
export const OFFER_PLATFORM_SHARE_BPS: Readonly<Record<OfferFunding, number>> = Object.freeze({
  SELLER: 0,
  SPLIT: 5000,
  PLATFORM: 10000,
});

/** One product in an offer. PERCENTAGE is in basis points (2000 = 20%); FLAT in paise. */
export const OfferItemSchema = z
  .object({
    productId: z.string().min(1),
    discountType: z.enum(['PERCENTAGE', 'FLAT']),
    discountAmount: z.number().int().positive(),
  })
  .strict()
  .refine((v) => v.discountType !== 'PERCENTAGE' || v.discountAmount < 10000, {
    message: 'A percentage discount must be less than 100%',
    path: ['discountAmount'],
  });
export type OfferItem = z.infer<typeof OfferItemSchema>;

/**
 * A seller's offer. The seller pays for it (SELLER) or asks Terra Shoppy to
 * share the cost 50-50 (SPLIT); either way it goes live only once approved.
 */
export const VendorOfferSchema = z
  .object({
    title: z.string().trim().min(3).max(120),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    funding: z.enum(['SELLER', 'SPLIT']),
    sellerNote: z.string().trim().max(500).optional(),
    items: z.array(OfferItemSchema).min(1).max(100),
  })
  .strict();
export type VendorOfferRequest = z.infer<typeof VendorOfferSchema>;

/** Approving may carry a note; rejecting must say why. */
export const ApproveOfferSchema = z
  .object({ note: z.string().trim().max(500).optional() })
  .strict();
export type ApproveOfferRequest = z.infer<typeof ApproveOfferSchema>;
export const RejectOfferSchema = z.object({ note: z.string().trim().min(3).max(500) }).strict();
export type RejectOfferRequest = z.infer<typeof RejectOfferSchema>;
